import { createContext, useContextSelector } from 'use-context-selector';
import type { SmsSessionGroup } from './sms-session-grouping';

/**
 * Channel+participation identifiers returned by startSmsSession when it just
 * created them (first session for this client); null on idempotent reopen.
 */
export interface StartedSmsParticipation {
  conversationParticipationId: string;
  conversationChannelId: string;
  externalIdentifier: string;
  /** The partner line the channel row carries — the first send must submit it verbatim. */
  internalIdentifier: string;
}

export interface SmsSessionContextValue {
  openSessionId: string | null;
  openSessionChannelId: string | null;
  openedAt: string | null;
  sessionGroups: SmsSessionGroup[];
  isLoading: boolean;
  /**
   * Opens a session (idempotent server-side) and patches the open-session cache. The channel id
   * pins the recipient the composer resolved; omitted, the backend picks the conversation's channel.
   */
  startSession: (conversationChannelId?: string) => Promise<StartedSmsParticipation | null>;
  endSession: () => Promise<void>;
  isEnding: boolean;
  showSmsSwitch: boolean;
  hasOpenSession: boolean;
  /** Non-null when the client's number is known non-sendable — disables the SMS tab. */
  smsDisabledReason: string | null;
  sendersByChannelId: ReadonlyMap<string, string>;
  requesterFirstName: string | null;
}

export const SmsSessionContext = createContext<SmsSessionContextValue>({
  openSessionId: null,
  openSessionChannelId: null,
  openedAt: null,
  sessionGroups: [],
  isLoading: false,
  startSession: () => Promise.resolve(null),
  endSession: () => Promise.resolve(),
  isEnding: false,
  showSmsSwitch: false,
  hasOpenSession: false,
  smsDisabledReason: null,
  sendersByChannelId: new Map(),
  requesterFirstName: null,
});

export function useSmsSessionContext<T>(selector: (v: SmsSessionContextValue) => T): T {
  return useContextSelector(SmsSessionContext, selector);
}
